// Audit-only PDF adapter. Uses the already rendered, selected evidence; no API calls or remote assets.
const CREDIT = 'Generated with: VisitOMS EarlyCare www.visitoms.com';
const WEBSITE = 'https://www.visitoms.com/';
const COLORS = { ink: '#1e293b', muted: '#64748b', line: '#e2e8f0', soft: '#f1f5f9', card: '#f8fafc', caution: '#fffbeb', cautionLine: '#fde68a', danger: '#fef2f2', dangerLine: '#fecaca' };
const TYPE = { title: 18, brand: 11, section: 12, subsection: 10, body: 9, table: 8.5, small: 7.5, metric: 16 };
// Preserve the web report's inline labels instead of flattening <strong> to plain text.
function richText(element, inherited = {}) {
  if (!element) return [];
  if (element.nodeType === 3) return [{ text: (element.nodeValue || '').replace(/→/g, ' -> '), ...inherited }];
  if (element.tagName === 'BUTTON' || element.classList?.contains('audit-print-only')) return [];
  if (element.tagName === 'BR') return [{ text: '\n', ...inherited }];
  const style = { ...inherited, ...(['STRONG', 'B'].includes(element.tagName) ? { bold: true } : {}) };
  const runs = Array.from(element.childNodes || []).flatMap(child => richText(child, style));
  return runs;
}
function panel(rows, fillColor, borderColor, extra = {}) {
  return {
    table: { widths: ['*'], headerRows: rows.length > 1 ? 1 : 0, keepWithHeaderRows: rows.length > 1 ? 1 : 0,
      body: rows.map(stack => [{ stack, fillColor }]) },
    layout: { hLineWidth: i => i === 0 || i === rows.length ? 0.5 : 0, vLineWidth: () => 0.5,
      hLineColor: () => borderColor, vLineColor: () => borderColor,
      paddingLeft: () => 9, paddingRight: () => 9, paddingTop: () => 7, paddingBottom: () => 7 },
    margin: [0, 7, 0, 9], ...extra,
  };
}

function text(element) {
  if (!element) return '';
  if (element.nodeType === 3) return element.nodeValue || '';
  if (element.tagName === 'BUTTON' || element.classList?.contains('audit-print-only')) return '';
  if (element.tagName === 'BR') return '\n';
  return Array.from(element.childNodes || []).map(child => {
    const value = text(child);
    return value && /^(P|DIV|LI|DT|DD|H[1-6])$/.test(child.tagName || '') ? `\n${value}\n` : value;
  }).join(' ').replace(/→/g, ' -> ').replace(/[ \t]+/g, ' ').replace(/ *\n */g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}
const paragraph = (value, extra = {}) => ({ text: value || '—', margin: [0, 0, 0, 5], ...extra });
function heading(value, id) {
  return { text: value, id, tocItem: true, headlineLevel: 1, fontSize: TYPE.section, bold: true, color: COLORS.ink, margin: [0, 13, 0, 6] };
}
const borderLayout = {
  hLineWidth: () => 0.4, vLineWidth: () => 0,
  hLineColor: () => COLORS.line,
  paddingLeft: () => 5, paddingRight: () => 5,
  paddingTop: () => 5, paddingBottom: () => 5,
};

function evidenceTable(section) {
  const source = section.querySelector('table');
  const title = text(section.querySelector('h3'));
  const note = text(Array.from(section.children).find(el => el.tagName === 'P'));
  if (!source) return [heading(title, section.id), paragraph(note), paragraph('No matching records available for this section.')];
  const header = Array.from(source.tHead.rows).find(row => !row.classList.contains('audit-print-only'));
  const count = header.cells.length;
  const widths = Array.from(source.querySelectorAll('col')).map(col => parseFloat(col.style.width));
  const totalWidth = 516; // Letter width minus 48pt margins on both sides, minus cell padding.
  const cellWidths = widths.length === count ? widths.map(w => Math.max(15, (totalWidth - 10 * count) * w / 100)) : Array(count).fill('*');
  const caption = { text: title, bold: true, fontSize: TYPE.subsection, id: section.id, tocItem: true };
  const body = [
    [{ stack: [caption, ...(note ? [paragraph(note, { fontSize: TYPE.small, color: COLORS.muted, margin: [0, 4, 0, 0] })] : [])], colSpan: count, fillColor: '#ffffff' }, ...Array.from({ length: count - 1 }, () => ({}))],
    Array.from(header.cells).map(cell => ({ text: text(cell), bold: true, fillColor: COLORS.soft })),
    ...Array.from(source.tBodies).flatMap(tbody => Array.from(tbody.rows).map(row => Array.from(row.cells).map(cell => ({ text: text(cell) || '—' })))),
  ];
  return [{ table: { headerRows: 2, keepWithHeaderRows: 1, widths: cellWidths, body }, layout: borderLayout, fontSize: TYPE.table, margin: [0, 10, 0, 8] }];
}
function visitOverview(section) {
  const cards = Array.from(section.querySelectorAll('.audit-visit-card'));
  const title = text(section.querySelector('h2'));
  const note = text(section.querySelector('p'));
  if (!cards.length) return [heading(title, 'audit-visit-overview'), paragraph('No selected visits in this report.')];
  return cards.map((card, index) => ({
    table: { widths: ['*'], headerRows: 1, keepWithHeaderRows: 1, body: [
      [{ stack: [
        ...(index === 0 ? [heading(title, 'audit-visit-overview'), paragraph(note, { fontSize: TYPE.small, color: COLORS.muted })] : []),
        { text: text(card.querySelector('h3')), bold: true },
      ], fillColor: COLORS.soft }],
      [{ stack: Array.from(card.querySelectorAll('dl > div')).map(field => ({
        text: [{ text: `${text(field.querySelector('dt'))}: `, bold: true }, { text: text(field.querySelector('dd')) }], margin: [0, 0, 0, 5],
      })) }],
    ] }, layout: borderLayout, fontSize: TYPE.body, margin: [0, 3, 0, 8],
  }));
}

function auditChanges(section) {
  const events = Array.from(section.querySelectorAll('.audit-event'));
  return [heading(text(section.querySelector('h3')), 'audit-changes'),
    paragraph(text(section.querySelector('p')), { fontSize: TYPE.small, color: COLORS.muted }),
    ...(!events.length ? [paragraph('No matching records available for this section.')] : []),
    ...events.map(card => ({
      table: { widths: ['*'], headerRows: 1, keepWithHeaderRows: 1, body: [
        [{ text: text(card.querySelector('h4')), bold: true, fillColor: COLORS.soft }],
        [{ stack: Array.from(card.children).filter(el => el.tagName !== 'H4').flatMap(el => el.tagName === 'DL'
          ? Array.from(el.children).map(field => ({ text: [{ text: `${text(field.querySelector('dt'))}: `, bold: true }, { text: text(field.querySelector('dd')) || '—' }], margin: [0, 0, 0, 3] }))
          : [paragraph(text(el))]) }],
      ] }, layout: borderLayout, fontSize: TYPE.table, margin: [0, 2, 0, 7],
    }))];
}
function logoData(header) {
  const image = header.querySelector('img');
  if (!image?.complete || !image.naturalWidth) return null;
  try {
    const canvas = image.ownerDocument.createElement('canvas');
    const scale = Math.min(1, 240 / Math.max(image.naturalWidth, image.naturalHeight));
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } catch { return null; } // Cross-origin logos may be unavailable; the organization name remains.
}
export function buildAuditPdfDefinition(article, manifest) {
  if (!article || !manifest?.report_id) throw new Error('Generate the report before exporting PDF.');
  const header = article.querySelector('header');
  const brandRow = header.firstElementChild;
  const brand = text(brandRow.querySelector('p'));
  const logo = logoData(header);
  const title = { stack: [paragraph('Student Service Audit Report', { fontSize: TYPE.title, bold: true }), paragraph(brand, { fontSize: TYPE.brand }), paragraph(CREDIT, { link: WEBSITE, color: COLORS.muted, fontSize: TYPE.small })] };
  const intro = Array.from(header.children).slice(1).flatMap(el => el.tagName === 'DIV'
    ? [{ columns: [0,1].map(column => ({ width: '*', stack: Array.from(el.children).filter((_,i) => i % 2 === column).map(p => paragraph(richText(p), { fontSize: TYPE.body })) })), columnGap: 12, margin: [0, 0, 0, 5] }]
    : [paragraph(richText(el), { fontSize: TYPE.body })]);
  // Place the existing closing metadata in the introduction: there is no metadata-only final page.
  const footer = article.querySelector('footer');
  const metadata = footer ? Array.from(footer.children).filter(p => !p.classList.contains('audit-end-brand')).map(p => paragraph(text(p), { fontSize: TYPE.small, color: COLORS.muted })) : [];
  const content = [logo ? { columns: [{ image: logo, fit: [42, 42], width: 48 }, title], columnGap: 8, margin: [0, 0, 0, 9] } : title, ...intro, ...metadata];
  for (const element of Array.from(article.children)) {
    if (element === header || element === footer) continue;
    if (element.tagName === 'NAV') {
      content.push({ toc: { title: { text: 'Report contents', fontSize: TYPE.section, bold: true, margin: [0, 10, 0, 6] } }, fontSize: TYPE.body, margin: [0, 0, 0, 10], pageBreak: 'before' });
    } else if (element.id === 'audit-visit-overview') content.push(...visitOverview(element));
    else if (element.id === 'audit-changes') content.push(...auditChanges(element));
    else if (element.classList.contains('audit-section') || element.querySelector('table')) content.push(...evidenceTable(element));
    else if (element.tagName === 'H2') content.push({ ...heading(text(element), element.id), pageBreak: 'before' });
    else if (element.tagName === 'SECTION') {
      const titleNode = element.querySelector('h3');
      if (titleNode) {
        const title = { ...heading(text(titleNode), element.id || undefined), margin: [0, 0, 0, 0] };
        const list = element.querySelector('ul');
        const details = list ? { ul: Array.from(list.children).map(li => ({ text: richText(li) })), fontSize: TYPE.body, margin: [0, 0, 0, 0] }
          : paragraph(text(element), { fontSize: TYPE.body });
        content.push(panel([[title], [details]], COLORS.caution, COLORS.cautionLine));
      } else if (element.classList.contains('grid')) content.push({
        columns: Array.from(element.children).map(card => panel([[
          paragraph(text(card.querySelector('strong')), { fontSize: TYPE.metric, bold: true, margin: [0, 0, 0, 5] }),
          paragraph(text(card.querySelector('span')), { fontSize: TYPE.body, margin: [0, 0, 0, 0] }),
        ]], COLORS.card, COLORS.line, { width: '*', margin: [0, 0, 0, 0] })), columnGap: 8, margin: [0, 8, 0, 10],
      });
      else {
        const warningTitle = element.querySelector('strong');
        const warningList = element.querySelector('ul');
        const details = warningList ? { ul: Array.from(warningList.children).map(li => ({ text: richText(li) })), fontSize: TYPE.body, color: '#991b1b' }
          : paragraph(richText(element), { fontSize: TYPE.body, color: '#991b1b', margin: [0, 0, 0, 0] });
        content.push(panel([...(warningTitle && warningList ? [[paragraph(text(warningTitle), { bold: true, color: '#991b1b', margin: [0, 0, 0, 0] })]] : []), [details]], COLORS.danger, COLORS.dangerLine));
      }
    }
  }
  return {
    pageSize: 'LETTER', pageMargins: [48, 40, 48, 56], compress: true,
    info: { title: 'Student Service Audit Report', author: manifest.generated_by?.name || 'VisitOMS EarlyCare', subject: `Report ${manifest.report_id}`, creator: 'VisitOMS EarlyCare' },
    defaultStyle: { font: 'Roboto', fontSize: TYPE.body, color: COLORS.ink, lineHeight: 1.15 },
    header: () => ({ text: `${manifest.date_from} — ${manifest.date_to} · Report ${manifest.report_id}`, fontSize: TYPE.small, color: COLORS.muted, margin: [48, 16, 48, 0] }),
    footer: (page, pages) => ({ stack: [
      { text: CREDIT, link: WEBSITE, color: '#94a3b8', fontSize: TYPE.small, alignment: 'center' },
      { text: `Page ${page} of ${pages}`, color: COLORS.muted, fontSize: TYPE.small, alignment: 'center', margin: [0, 4, 0, 0] },
    ], margin: [48, 12, 48, 0] }),
    pageBreakBefore: (node, followingNodes) => node.headlineLevel === 1 && followingNodes.length === 0,
    content,
  };
}
export async function createAuditPdfBlob(article, manifest) {
  const definition = buildAuditPdfDefinition(article, manifest);
  // Lazy, locally bundled assets. No CDN, remote font lookup or patient-data upload.
  const [pdfModule, fontModule] = await Promise.all([import('./vendor/pdfmake-0.2.20'), import('./vendor/pdfmake-fonts-0.2.20')]);
  const pdfMake = pdfModule.default || pdfModule;
  pdfMake.addVirtualFileSystem(fontModule.default || fontModule);
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('PDF generation timed out. Narrow the report period and retry.')), 120000);
    try { pdfMake.createPdf(definition).getBlob(blob => { clearTimeout(timeout); resolve(blob); }); } catch (error) { clearTimeout(timeout); reject(error); }
  });
}
